import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
  useContext,
} from "react";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  getAPICall,
  postAPICall,
  putAPICall,
  deleteAPICall,
  postFormDataAPICall,
} from "../../config/apiCalls";
import { UserContext } from "../../context/UserContext";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import XLSX from "xlsx-js-style";
import {
  BorderStyle,
  Document,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import "./ScopeOfWork.css";
import DesignCategoriesEditAdd from "../DesignCategories/DesignCategoriesEditAdd/DesignCategoriesEditAdd";
import AssembliesConsoleEditAdd from "../Admin/AssembliesConsole/AssembliesConsoleEditAdd/AssembliesConsoleEditAdd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  DecoupledEditor,
  Essentials,
  Paragraph as CKParagraph,
  GeneralHtmlSupport,
  Style,
  RemoveFormat,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import DOMPurify from "dompurify";

// ─── Static options ───────────────────────────────────────────────────────────

function formatSaveTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

// Rich-text (Description of Work) is stored as HTML but exports stay plain text.
function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || "";
}

function suggestKeynote(rows, letter = "D") {
  let max = 0;
  for (const r of rows) {
    const m = /^([A-Z]+)(\d+)$/.exec((r.keynote || "").trim());
    if (m && m[1] === letter) max = Math.max(max, parseInt(m[2], 10));
  }
  return letter + (max + 1);
}

function groupRows(rows) {
  const asms = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    let a = asms[asms.length - 1];
    const newGroup = !a || a.assembly_id !== r.assembly_id || a.assembly !== r.assembly;
    if (newGroup) {
      a = {
        assembly: r.assembly,
        assembly_id: r.assembly_id || null,
        assembly_type_id: r.assembly_type_id || null,
        assembly_type_name: r.assembly_type_name || "",
        keynote_letter: r.keynote_letter || "D",
        scope_of_work_order: r.scope_of_work_order ?? 0,
        keynotes: [],
        totalRows: 0,
      };
      asms.push(a);
    }
    let k = a.keynotes[a.keynotes.length - 1];
    if (!k || k.keynote !== r.keynote) {
      k = { keynote: r.keynote, indexes: [] };
      a.keynotes.push(k);
    }
    k.indexes.push(i);
    a.totalRows++;
  }
  return asms;
}

function groupTypes(asms) {
  const types = [];
  for (let ai = 0; ai < asms.length; ai++) {
    const a = asms[ai];
    let t = types[types.length - 1];
    if (!t || t.assembly_type_id !== a.assembly_type_id) {
      t = {
        assembly_type_id: a.assembly_type_id,
        assembly_type_name: a.assembly_type_name,
        keynote_letter: a.keynote_letter,
        totalRows: 0,
        assemblies: [],
      };
      types.push(t);
    }
    t.assemblies.push({ ...a, ai });
    t.totalRows += a.totalRows;
  }
  return types;
}

function insertAssemblyRow(rs, newRow) {
  const newTypeId = newRow.assembly_type_id;
  const newOrder = newRow.scope_of_work_order ?? 0;

  // Same type already in table — insert after the last row of that type
  if (newTypeId != null) {
    for (let i = rs.length - 1; i >= 0; i--) {
      if (rs[i].assembly_type_id === newTypeId) {
        const next = rs.slice();
        next.splice(i + 1, 0, newRow);
        return next;
      }
    }
  }

  // New type — find first row whose type has a higher scope_of_work_order
  for (let i = 0; i < rs.length; i++) {
    if ((rs[i].scope_of_work_order ?? 0) > newOrder) {
      const next = rs.slice();
      next.splice(i, 0, newRow);
      return next;
    }
  }

  return [...rs, newRow];
}

function renumberKeynotes(rows) {
  // Assign sequential numbers to each distinct keynote, per letter prefix, in visual order.
  const seen = new Map(); // `${letter}_${old_keynote}` → new_keynote
  const counters = {}; // letter → next integer
  for (const row of rows) {
    const letter = row.keynote_letter || "D";
    const keynote = (row.keynote || "").trim();
    const m = /^([A-Z]+)(\d+)$/.exec(keynote);
    if (!m || m[1] !== letter) continue;
    const key = `${letter}_${keynote}`;
    if (!seen.has(key)) {
      counters[letter] = (counters[letter] || 0) + 1;
      seen.set(key, letter + counters[letter]);
    }
  }
  return rows.map((row) => {
    const letter = row.keynote_letter || "D";
    const keynote = (row.keynote || "").trim();
    const m = /^([A-Z]+)(\d+)$/.exec(keynote);
    if (!m || m[1] !== letter) return row;
    const newKeynote = seen.get(`${letter}_${keynote}`);
    return newKeynote && newKeynote !== keynote ? { ...row, keynote: newKeynote } : row;
  });
}

// Moves line `rowIdx` to slot q of keynote group g within `asm` (a groupRows
// entry). q counts the group's lines excluding the moved one. An assembly's
// rows are contiguous, so only that range is rewritten: every line gets its
// group's keynote and a fresh sort_order so the order persists. A group the
// move empties disappears; renumberKeynotes (applied by setRows) closes the gap.
function moveRowInAssembly(rows, asm, rowIdx, g, q) {
  const groups = asm.keynotes.map((kn) => kn.indexes.filter((i) => i !== rowIdx));
  groups[g].splice(q, 0, rowIdx);
  const start = asm.keynotes[0].indexes[0];
  const next = rows.slice();
  let newIdx = start;
  let k = start;
  groups.forEach((idxs, gi) => {
    idxs.forEach((i, pos) => {
      if (i === rowIdx) newIdx = k;
      next[k++] = { ...rows[i], keynote: asm.keynotes[gi].keynote, sort_order: pos + 1 };
    });
  });
  return { rows: next, newIdx };
}

// Moves keynote group `from` of `asm` to slot `to` (0..n, meaning "before
// group `to`"; n = after the last). Lines keep their keynote and sort_order —
// renumberKeynotes (applied by setRows) renumbers the groups to the new order.
function moveKeynoteInAssembly(rows, asm, from, to) {
  const order = asm.keynotes.map((_, i) => i);
  order.splice(from, 1);
  order.splice(to > from ? to - 1 : to, 0, from);
  const start = asm.keynotes[0].indexes[0];
  const next = rows.slice();
  let newIdx = start;
  let k = start;
  order.forEach((gi) => {
    if (gi === from) newIdx = k;
    asm.keynotes[gi].indexes.forEach((i) => {
      next[k++] = rows[i];
    });
  });
  return { rows: next, newIdx };
}

function blankRow(rows, nearIdx) {
  const near = rows[nearIdx];
  const letter = near?.keynote_letter || "D";
  return {
    id: makeId(),
    assembly: near?.assembly || "",
    assembly_id: near?.assembly_id || null,
    assembly_type_id: near?.assembly_type_id || null,
    assembly_type_name: near?.assembly_type_name || "",
    keynote_letter: letter,
    scope_of_work_order: near?.scope_of_work_order ?? 0,
    keynote: suggestKeynote(rows, letter),
    sort_order: 0,
    category: "",
    element: "",
    core_repair_scope_id: null,
    building_element_id: null,
    description: "",
    material: "",
    specs: [],
    details: "",
  };
}

// ─── useHistory ───────────────────────────────────────────────────────────────

function useHistory(initial) {
  const [past, setPast] = useState([]);
  const [present, setPresent] = useState(initial);
  const [future, setFuture] = useState([]);

  const set = useCallback((updater) => {
    setPresent((curr) => {
      const next = typeof updater === "function" ? updater(curr) : updater;
      if (next === curr) return curr;
      setPast((p) => [...p, curr].slice(-100));
      setFuture([]);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      setFuture((f) => [present, ...f]);
      setPresent(prev);
      return p.slice(0, -1);
    });
  }, [present]);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setPast((p) => [...p, present]);
      setPresent(next);
      return f.slice(1);
    });
  }, [present]);

  const reset = useCallback((next) => {
    setPast([]);
    setPresent(typeof next === "function" ? next(present) : next);
    setFuture([]);
  }, [present]);

  return {
    state: present,
    set,
    reset,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
  };
}

// ─── Cell display ─────────────────────────────────────────────────────────────

function CellView({ col, value, isLocked }) {
  if (col.type === "select" && col.pill) {
    if (!value) return <span className="sow-placeholder">Pick…</span>;
    return (
      <span className={`sow-pill sow-pill-${pillClass(value)}`}>{value}</span>
    );
  }
  if (col.type === "select") {
    if (!value) return <span className="sow-placeholder">Pick…</span>;
    return <span>{value}</span>;
  }
  if (col.type === "scope") {
    if (!value) return <span className="sow-placeholder">Pick scope…</span>;
    return (
      <span className="sow-scope-display">
        <span className="sow-scope-dot" />
        {value}
      </span>
    );
  }
  if (col.type === "spec") {
    if (!value?.length) return <span className="sow-placeholder">{isLocked ? "Pick a scope to fill…" : "Pick spec…"}</span>;
    return (
      <span>
        {value.map((s) => (
          <span key={s.id} style={{ display: "block" }}>{s.label}</span>
        ))}
      </span>
    );
  }
  if (col.type === "richtext") {
    const html = (value ?? "").toString();
    if (!html)
      return <span className="sow-placeholder">{isLocked ? "Pick a scope to fill…" : placeholderFor(col)}</span>;
    return (
      <span
        className="sow-richtext-view"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
    );
  }
  const txt = (value ?? "").toString();
  if (!txt)
    return <span className="sow-placeholder">{isLocked ? "Pick a scope to fill…" : placeholderFor(col)}</span>;
  return <span style={{ whiteSpace: "pre-wrap" }}>{txt}</span>;
}

function pillClass() {
  return "default";
}

function placeholderFor(col) {
  switch (col.key) {
    case "description":
      return "Pick a scope to fill…";
    case "material":
      return "—";
    case "specs":
      return "Pick spec…";
    case "details":
      return "Add details…";
    default:
      return "—";
  }
}

// ─── Cell editors ─────────────────────────────────────────────────────────────

function TextEditor({ value, onCommit, onCancel, maxLength }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);
  return (
    <input
      ref={ref}
      className="sow-cell-input"
      defaultValue={value ?? ""}
      maxLength={maxLength}
      onBlur={(e) => onCommit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit(e.currentTarget.value);
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        } else if (e.key === "Tab") {
          e.preventDefault();
          onCommit(e.currentTarget.value, e.shiftKey ? "left" : "right");
        }
      }}
    />
  );
}

function LongTextEditor({ value, onCommit, onCancel, maxLength }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    autosize(el);
  }, []);
  function autosize(el) {
    el.style.height = "auto";
    el.style.height = Math.max(64, el.scrollHeight) + "px";
  }
  return (
    <textarea
      ref={ref}
      className="sow-cell-textarea"
      defaultValue={value ?? ""}
      maxLength={maxLength}
      onInput={(e) => autosize(e.currentTarget)}
      onBlur={(e) => onCommit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onCommit(e.currentTarget.value);
        }
      }}
    />
  );
}

// Same style definitions as SpecTextEditor.js — same class names/colors so
// highlighted text looks consistent with the rest of the app.
const RICH_TEXT_STYLE_DEFINITIONS = [
  { name: "Bold Text", element: "span", classes: ["bold"] },
  { name: "Red Text", element: "span", classes: ["red"] },
  { name: "Yellow Highlight", element: "span", classes: ["yellow-highlight"] },
  { name: "Gray Highlight", element: "span", classes: ["gray-highlight"] },
  { name: "Blue Highlight", element: "span", classes: ["blue-highlight"] },
  { name: "Green Highlight", element: "span", classes: ["green-highlight"] },
  { name: "Pink Highlight", element: "span", classes: ["pink-highlight"] },
  { name: "Uppercase Text", element: "span", classes: ["uppercase"] },
  { name: "Italic Text", element: "span", classes: ["italic"] },
  { name: "Underline Text", element: "span", classes: ["underline"] },
];

// Typing happens inline in the cell (a DecoupledEditor's editable area, styled
// to blend in like the plain textarea editors); the Style/formatting toolbar is
// split off and floated in its own popover below the cell.
function RichTextEditor({ value, onCommit, anchorRef }) {
  const toolbarPopoverRef = useRef(null);
  const latestRef = useRef(value ?? "");

  // Unlike the picker popovers, this one stays open while the cell grows with
  // typed text and while the table scrolls, so re-anchor it on every layout
  // change instead of only on mount — otherwise it sits over the text.
  useLayoutEffect(() => {
    const el = toolbarPopoverRef.current;
    const anchor = anchorRef?.current;
    if (!el || !anchor) return;
    function position() {
      const r = anchor.getBoundingClientRect();
      el.style.position = "fixed";
      el.style.left = `${r.left}px`;
      el.style.minWidth = `${Math.max(r.width, 220)}px`;
      el.style.zIndex = "1300";
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      if (spaceBelow >= 44 || spaceBelow >= spaceAbove) {
        el.style.top = `${r.bottom + 2}px`;
        el.style.bottom = "auto";
      } else {
        el.style.bottom = `${window.innerHeight - r.top + 2}px`;
        el.style.top = "auto";
      }
    }
    position();
    let frame = 0;
    function schedule() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(position);
    }
    const ro = new ResizeObserver(schedule);
    ro.observe(anchor);
    // capture: scroll events don't bubble, and the grid scrolls inside
    // .sow-grid-wrap rather than the window
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [anchorRef]);

  // Clicking into the floating toolbar is a separate DOM subtree from the
  // inline editable, so commit-on-blur (used by the other editors) doesn't
  // work here — commit only once a click lands outside BOTH pieces, same
  // pattern as ContextMenu's outside-click handling elsewhere in this file.
  useEffect(() => {
    function onDown(e) {
      const inAnchor = anchorRef?.current?.contains(e.target);
      const inToolbar = toolbarPopoverRef.current?.contains(e.target);
      if (!inAnchor && !inToolbar) onCommit(latestRef.current);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onCommit, anchorRef]);

  return (
    <>
      <div className="sow-richtext-inline">
        <CKEditor
          editor={DecoupledEditor}
          data={value ?? ""}
          onReady={(editor) => {
            if (toolbarPopoverRef.current) {
              toolbarPopoverRef.current.appendChild(editor.ui.view.toolbar.element);
            }
            editor.editing.view.focus();
          }}
          config={{
            plugins: [Essentials, CKParagraph, GeneralHtmlSupport, Style, RemoveFormat],
            toolbar: { items: ["style", "removeFormat"] },
            style: { definitions: RICH_TEXT_STYLE_DEFINITIONS },
          }}
          onChange={(event, editor) => {
            latestRef.current = editor.getData();
          }}
        />
      </div>
      <div ref={toolbarPopoverRef} className="sow-richtext-toolbar-popover" />
    </>
  );
}

function SelectEditor({ value, options, onCommit, onCancel, anchorRef, onCreateNew, createNewLabel, searchPlaceholder }) {
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      el.style.position = "fixed";
      el.style.left = `${r.left}px`;
      el.style.minWidth = `${r.width}px`;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      if (spaceBelow >= el.offsetHeight + 2 || spaceBelow >= spaceAbove) {
        el.style.top = `${r.bottom + 2}px`;
        el.style.bottom = "auto";
      } else {
        el.style.bottom = `${window.innerHeight - r.top + 2}px`;
        el.style.top = "auto";
      }
    }
    if (onCreateNew) inputRef.current?.focus();
    else el?.focus();
  }, []);

  const filtered = query.trim()
    ? options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  return (
    <div
      className="sow-popover"
      ref={ref}
      tabIndex={-1}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) onCancel();
      }}
    >
      {onCreateNew && (
        <div className="sow-popover-search">
          <input
            ref={inputRef}
            className="sow-popover-search-input"
            placeholder={searchPlaceholder || "Search…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); onCancel(); }
            }}
          />
        </div>
      )}
      <div className="sow-popover-scroll">
        {filtered.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`sow-popover-item ${opt === value ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onCommit(opt);
            }}
          >
            {opt}
            {opt === value && (
              <CheckIcon
                sx={{ fontSize: 18, marginLeft: "auto", color: "var(--sow-primary)" }}
              />
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="sow-popover-empty">No matches</div>
        )}
      </div>
      {onCreateNew && (
        <>
          <div className="sow-menu-sep" />
          <button
            type="button"
            className="sow-popover-item sow-popover-create-new"
            onMouseDown={(e) => {
              e.preventDefault();
              onCreateNew();
            }}
          >
            {createNewLabel || "+ Create new"}
          </button>
        </>
      )}
    </div>
  );
}

function ScopePickerEditor({ value, scopes, onCommit, onCancel, anchorRef, currentScopeId }) {
  const [query, setQuery] = useState("");
  // Start on the current scope so arrow keys / Enter continue from it
  const [activeIdx, setActiveIdx] = useState(() =>
    Math.max(0, scopes.findIndex((s) => s.id === currentScopeId))
  );
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el && anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      el.style.position = "fixed";
      el.style.left = `${r.left}px`;
      el.style.minWidth = `${r.width}px`;
      el.style.zIndex = "1300";
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      if (spaceBelow >= el.offsetHeight + 2 || spaceBelow >= spaceAbove) {
        el.style.top = `${r.bottom + 2}px`;
        el.style.bottom = "auto";
      } else {
        el.style.bottom = `${window.innerHeight - r.top + 2}px`;
        el.style.top = "auto";
      }
    }
    // Open scrolled to the current scope (centered) instead of the list top.
    // Adjust the list's own scrollTop — scrollIntoView would also scroll the
    // grid/page behind this fixed popover.
    const list = el?.querySelector(".sow-scope-list");
    const item = list?.querySelector(".sow-scope-item.active");
    if (list && item) {
      const lr = list.getBoundingClientRect();
      const ir = item.getBoundingClientRect();
      list.scrollTop += ir.top - lr.top - (lr.height - ir.height) / 2;
    }
    inputRef.current?.focus();
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? scopes.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.buildingType?.toLowerCase().includes(q) ||
          s.material?.toLowerCase().includes(q)
      )
    : scopes;

  function commitScope(scope) {
    onCommit({
      __kind: "scope",
      id: scope.id,
      buildingElementId: scope.buildingElementId,
      name: scope.buildingType || "",
      description: scope.description,
      material: scope.material,
      specs: scope.specs || [],
    });
  }

  return (
    <div
      ref={containerRef}
      className="sow-scope-picker"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) onCancel();
      }}
    >
      <div className="sow-scope-search">
        <SearchIcon
          sx={{ fontSize: 20, color: "var(--sow-text-secondary)", flexShrink: 0 }}
        />
        <input
          ref={inputRef}
          className="sow-scope-search-input"
          placeholder="Search repair scopes…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIdx(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (filtered[activeIdx]) commitScope(filtered[activeIdx]);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
        />
      </div>
      <div className="sow-scope-list" tabIndex={-1}>
        {filtered.length === 0 && (
          <div className="sow-scope-empty">
            No matching scopes. To create a new scope use the <strong>Core Repair Scopes</strong> page.
          </div>
        )}
        {filtered.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`sow-scope-item ${i === activeIdx ? "hover" : ""} ${
              s.id === currentScopeId ? "active" : ""
            }`}
            onMouseEnter={() => setActiveIdx(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              commitScope(s);
            }}
          >
            <div className="sow-scope-name">
              <span className="sow-scope-dot" />
              {s.buildingType || s.name}
              {s.id === currentScopeId && (
                <CheckIcon
                  sx={{ fontSize: 16, marginLeft: "auto", color: "var(--sow-primary)" }}
                />
              )}
            </div>
            {s.name && (
              <div className="sow-scope-desc">{s.name}</div>
            )}
            {s.material && (
              <div className="sow-scope-meta">{s.material}</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function SpecPickerEditor({ value, specs, allSpecs, onCommit, onCancel, anchorRef, onDraftChange }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [selected, setSelected] = useState(
    () => new Set((value || []).map((s) => s.id))
  );
  const [mode, setMode] = useState("crs");
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    const anchor = anchorRef?.current;
    if (el && anchor) {
      const CHROME = 88; // search bar + footer (non-list chrome)
      const GAP = 6;
      const listEl = el.querySelector(".sow-scope-list");
      let below = null; // side is picked once so the picker doesn't flip mid-use

      // The cell previews ticked specs live, so it grows while the picker is
      // open — re-anchor to its edge whenever it resizes.
      const position = () => {
        const r = anchor.getBoundingClientRect();
        el.style.position = "fixed";
        el.style.left = `${r.left}px`;
        el.style.minWidth = `${r.width}px`;
        el.style.zIndex = "1300";
        const spaceBelow = window.innerHeight - r.bottom - GAP;
        const spaceAbove = r.top - GAP;
        if (below === null) below = spaceBelow >= spaceAbove;
        if (below) {
          el.style.top = `${r.bottom + GAP / 2}px`;
          el.style.bottom = "auto";
          if (listEl) listEl.style.maxHeight = `${Math.max(80, spaceBelow - CHROME)}px`;
        } else {
          el.style.bottom = `${window.innerHeight - r.top + GAP / 2}px`;
          el.style.top = "auto";
          if (listEl) listEl.style.maxHeight = `${Math.max(80, spaceAbove - CHROME)}px`;
        }
      };
      position();
      const ro = new ResizeObserver(position);
      ro.observe(anchor);
      inputRef.current?.focus();
      return () => ro.disconnect();
    }
    inputRef.current?.focus();
  }, []);

  const activeSpecs = mode === "all" ? allSpecs : specs;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? activeSpecs.filter(
        (s) =>
          s.division.toLowerCase().includes(q) ||
          s.title.toLowerCase().includes(q)
      )
    : activeSpecs;

  function specLabel(s) {
    return `${s.division} — ${s.title}`;
  }

  function toggle(s) {
    const next = new Set(selected);
    if (next.has(s.id)) next.delete(s.id);
    else next.add(s.id);
    setSelected(next);
    // Specs only commit when the picker closes; preview the ticks in the cell meanwhile
    onDraftChange?.(selectedSpecs(next));
  }

  function selectedSpecs(ids) {
    const seen = new Set();
    const combined = [...(allSpecs || []), ...(specs || [])].filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
    return combined
      .filter((s) => ids.has(s.id))
      .map((s) => ({ id: s.id, label: specLabel(s) }));
  }

  function commitSelected() {
    onCommit(selectedSpecs(selected));
  }

  return (
    <div
      ref={containerRef}
      className="sow-scope-picker"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) commitSelected();
      }}
    >
      <div className="sow-scope-search">
        <SearchIcon
          sx={{ fontSize: 20, color: "var(--sow-text-secondary)", flexShrink: 0 }}
        />
        <input
          ref={inputRef}
          className="sow-scope-search-input"
          placeholder="Search specifications…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIdx(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (filtered[activeIdx]) toggle(filtered[activeIdx]);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
        />
      </div>
      <div className="sow-scope-list" tabIndex={-1}>
        {filtered.length === 0 && (
          <div className="sow-scope-empty">
            No matching specifications. To create a new spec use the <strong>Spec Library</strong>.
          </div>
        )}
        {filtered.map((s, i) => {
          const isSelected = selected.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              className={`sow-scope-item ${i === activeIdx ? "hover" : ""}`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                toggle(s);
              }}
            >
              <div className="sow-spec-row">
                <span className={`sow-spec-cb${isSelected ? " sow-spec-cb-checked" : ""}`} />
                <div>
                  <div className="sow-scope-name">{s.division}</div>
                  <div className="sow-scope-desc">{s.title}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="sow-scope-footer">
        <button
          type="button"
          className="sow-spec-mode-toggle"
          onMouseDown={(e) => {
            e.preventDefault();
            setMode((m) => (m === "crs" ? "all" : "crs"));
            setQuery("");
            setActiveIdx(0);
          }}
        >
          {mode === "all" ? "Show CRS specs only" : "Show all specs"}
        </button>
      </div>
    </div>
  );
}

// ─── Cell component ───────────────────────────────────────────────────────────

function renderEditor(col, value, onCommit, onCancel, anchorRef, specOverride, currentScopeId, onCreateNew, onDraftChange) {
  switch (col.type) {
    case "longtext":
      return (
        <LongTextEditor value={value} onCommit={onCommit} onCancel={onCancel} maxLength={col.maxLength} />
      );
    case "richtext":
      return (
        <RichTextEditor value={value} onCommit={onCommit} anchorRef={anchorRef} />
      );
    case "select":
      return (
        <SelectEditor
          value={value}
          options={col.options}
          onCommit={onCommit}
          onCancel={onCancel}
          anchorRef={anchorRef}
          onCreateNew={onCreateNew}
          createNewLabel={col.createNewLabel}
          searchPlaceholder={col.searchPlaceholder}
        />
      );
    case "scope":
      return (
        <ScopePickerEditor
          value={value}
          scopes={col.scopes}
          onCommit={onCommit}
          onCancel={onCancel}
          anchorRef={anchorRef}
          currentScopeId={currentScopeId}
        />
      );
    case "spec":
      return (
        <SpecPickerEditor
          value={value}
          specs={specOverride ?? col.specs}
          allSpecs={col.specs}
          onCommit={onCommit}
          onCancel={onCancel}
          anchorRef={anchorRef}
          onDraftChange={onDraftChange}
        />
      );
    default:
      return (
        <TextEditor value={value} onCommit={onCommit} onCancel={onCancel} maxLength={col.maxLength} />
      );
  }
}

function Cell({
  col,
  colIdx,
  rowIdx,
  value,
  row,
  selection,
  setSelection,
  editing,
  setEditing,
  onCommit,
  onCreateNew,
  reorderBtns,
}) {
  const selected =
    selection && selection.r === rowIdx && selection.c === colIdx;
  const isEditing =
    editing && editing.r === rowIdx && editing.c === colIdx;
  const isLocked =
    (col.key === "description" || col.key === "material" || col.key === "specs") && !row.core_repair_scope_id;
  const tdRef = useRef(null);
  const clickOpensRef = useRef(false);

  useEffect(() => {
    if (selected && !isEditing) tdRef.current?.focus({ preventScroll: true });
  }, [selected, isEditing]);

  function onCellCommit(val, dir) {
    onCommit(rowIdx, colIdx, val, dir);
  }

  const specOverride = col.key === "specs" ? (row.availableSpecs || []) : null;

  // Pickers float in a popover, so keep the cell's current value visible
  // underneath while they're open (text editors replace the content instead)
  const keepsContent = col.type === "select" || col.type === "scope" || col.type === "spec";
  const [draftValue, setDraftValue] = useState(null);
  useEffect(() => {
    if (!isEditing) setDraftValue(null);
  }, [isEditing]);

  const content = (
    <div className={`sow-cell-content${reorderBtns ? " sow-cell-content-with-reorder" : ""}`}>
      {reorderBtns}
      <CellView col={col} value={draftValue ?? value} isLocked={isLocked} />
    </div>
  );

  return (
    <td
      ref={tdRef}
      tabIndex={-1}
      className={`sow-cell sow-cell-${col.type} ${selected ? "selected" : ""} ${
        isEditing ? "editing" : ""
      } ${isLocked ? "sow-cell-locked" : ""}`}
      onMouseDown={(e) => {
        // Only a press that starts on the closed cell (not inside an open
        // editor/picker, not on the drag handle) may open it on click —
        // otherwise picking an option would close the picker and reopen it.
        clickOpensRef.current = !isEditing && !e.target.closest("button");
        if (!isEditing) setSelection({ r: rowIdx, c: colIdx });
      }}
      onClick={() => {
        if (clickOpensRef.current && !isLocked) setEditing({ r: rowIdx, c: colIdx });
        clickOpensRef.current = false;
      }}
    >
      {isEditing && !isLocked ? (
        <>
          {keepsContent && content}
          {renderEditor(col, value, onCellCommit, () => setEditing(null), tdRef, specOverride, row.core_repair_scope_id, col.createNew && onCreateNew ? () => { setEditing(null); onCreateNew(rowIdx); } : undefined, setDraftValue)}
        </>
      ) : (
        content
      )}
    </td>
  );
}

// ─── NotUsedCells ─────────────────────────────────────────────────────────────

function NotUsedCells({
  columns,
  rowIdx,
  row,
  setRows,
  setSelection,
  selection,
  setEditing,
  editing,
  commitCell,
  onCategoryCreateNew,
  reorderBtns,
}) {
  return (
    <>
      <Cell
        col={columns[0]}
        colIdx={0}
        rowIdx={rowIdx}
        value={row.category}
        row={row}
        selection={selection}
        setSelection={setSelection}
        editing={editing}
        setEditing={setEditing}
        onCommit={commitCell}
        onCreateNew={onCategoryCreateNew}
        reorderBtns={reorderBtns}
      />
      <td
        className="sow-td-notused"
        colSpan={columns.length - 1}
        onClick={() => {
          setSelection({ r: rowIdx, c: 1 });
          setRows((rs) => {
            const next = rs.slice();
            next[rowIdx] = { ...next[rowIdx], notUsed: false };
            return next;
          });
        }}
        title="Click to populate"
      >
        <span className="sow-notused-pill">NOT USED</span>
        <span className="sow-notused-hint">click to populate</span>
      </td>
    </>
  );
}

// ─── ScopeTable ───────────────────────────────────────────────────────────────

function ScopeTable({
  columns,
  rows,
  setRows,
  selection,
  setSelection,
  editing,
  setEditing,
  checkedRows,
  setCheckedRows,
  onContextRow,
  onContextGroup,
  onCategoryCreateNew,
}) {
  const asms = useMemo(() => groupRows(rows), [rows]);
  const types = useMemo(() => groupTypes(asms), [asms]);
  const numCols = columns.length;
  const [movedRowIds, setMovedRowIds] = useState(new Set());
  const moveAnimTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(moveAnimTimerRef.current), []);

  function flashRows(ids) {
    if (moveAnimTimerRef.current) clearTimeout(moveAnimTimerRef.current);
    setMovedRowIds(new Set(ids));
    moveAnimTimerRef.current = setTimeout(() => setMovedRowIds(new Set()), 600);
  }

  function commitCell(r, c, value, dir) {
    const col = columns[c];
    setRows((rs) => {
      const next = rs.slice();
      if (
        col.type === "scope" &&
        value &&
        typeof value === "object" &&
        value.__kind === "scope"
      ) {
        next[r] = {
          ...next[r],
          element: value.name,
          core_repair_scope_id: value.id,
          building_element_id: value.buildingElementId,
          description: value.description,
          material: value.material,
          availableSpecs: value.specs || [],
          specs: [],
        };
      } else if (col.type === "spec" && Array.isArray(value)) {
        next[r] = { ...next[r], specs: value };
      } else {
        next[r] = { ...next[r], [col.key]: value };
      }
      return next;
    });
    setEditing(null);
    if (dir === "right") moveSelection(r, c, 0, 1);
    else if (dir === "left") moveSelection(r, c, 0, -1);
  }

  function commitKeynote(assemblyIdx, keynoteIdx, value) {
    setRows((rs) => {
      const kn = asms[assemblyIdx]?.keynotes[keynoteIdx];
      if (!kn) return rs;
      const next = rs.slice();
      kn.indexes.forEach((i) => {
        next[i] = { ...next[i], keynote: value };
      });
      return next;
    });
    setEditing(null);
  }

  function addSubRow(assemblyIdx, keynoteIdx) {
    const kn = asms[assemblyIdx].keynotes[keynoteIdx];
    const a = asms[assemblyIdx];
    const insertAt = kn.indexes[kn.indexes.length - 1] + 1;
    const template = rows[kn.indexes[0]];
    const maxSortOrder = Math.max(0, ...kn.indexes.map((i) => rows[i].sort_order ?? 0));
    setRows((rs) => {
      const next = rs.slice();
      next.splice(insertAt, 0, {
        id: makeId(),
        assembly: a.assembly,
        assembly_id: template?.assembly_id || null,
        assembly_type_id: a.assembly_type_id || null,
        assembly_type_name: a.assembly_type_name || "",
        keynote_letter: a.keynote_letter || "D",
        scope_of_work_order: a.scope_of_work_order ?? 0,
        keynote: kn.keynote,
        sort_order: maxSortOrder + 1,
        category: template?.category || "",
        element: "",
        core_repair_scope_id: null,
        building_element_id: null,
        description: "",
        material: "",
        specs: [],
        details: "",
      });
      return next;
    });
  }

  function addKeynote(assemblyIdx) {
    const a = asms[assemblyIdx];
    const lastKn = a.keynotes[a.keynotes.length - 1];
    const insertAt = lastKn.indexes[lastKn.indexes.length - 1] + 1;
    const template = rows[a.keynotes[0].indexes[0]];
    const letter = a.keynote_letter || "D";
    setRows((rs) => {
      const next = rs.slice();
      next.splice(insertAt, 0, {
        id: makeId(),
        assembly: a.assembly,
        assembly_id: template?.assembly_id || null,
        assembly_type_id: a.assembly_type_id || null,
        assembly_type_name: a.assembly_type_name || "",
        keynote_letter: letter,
        scope_of_work_order: a.scope_of_work_order ?? 0,
        keynote: suggestKeynote(rs, letter),
        sort_order: 0,
        category: "",
        element: "",
        core_repair_scope_id: null,
        building_element_id: null,
        description: "",
        material: "",
        specs: [],
        details: "",
      });
      return next;
    });
  }

  // Moves keynote group ki to slot `to` (0..n: "before group `to`") of its
  // assembly; setRows' renumberKeynotes then renumbers to match the new order.
  function moveKeynote(ai, ki, to) {
    if (to === ki || to === ki + 1) return;
    const { newIdx } = moveKeynoteInAssembly(rows, asms[ai], ki, to);
    setEditing(null);
    // Rebuild from the latest rows, not this render's: pressing a drag handle
    // commits any open cell editor *after* the drag captured `rows`, and moves
    // don't change indexes, so recomputing keeps that edit.
    setRows((rs) => moveKeynoteInAssembly(rs, groupRows(rs)[ai], ki, to).rows);
    flashRows(asms[ai].keynotes[ki].indexes.map((i) => rows[i].id));
    setSelection({ r: newIdx, c: 0 });
  }

  // ── Moving a line within its assembly ──────────────────────────────────────
  // A line can move anywhere inside its own assembly, including into another
  // keynote group (it takes that group's keynote). It never leaves the
  // assembly — that would change what the line is. Positions are (g, q):
  // keynote group g, slot q among that group's lines *without* the moved one.

  function findRowSlot(ai, rowIdx) {
    const g = asms[ai].keynotes.findIndex((kn) => kn.indexes.includes(rowIdx));
    return { g, p: asms[ai].keynotes[g].indexes.indexOf(rowIdx) };
  }

  function isSameSlot(ai, rowIdx, g, q) {
    const cur = findRowSlot(ai, rowIdx);
    return cur.g === g && cur.p === q;
  }

  function moveRow(ai, rowIdx, g, q) {
    if (isSameSlot(ai, rowIdx, g, q)) return;
    const { newIdx } = moveRowInAssembly(rows, asms[ai], rowIdx, g, q);
    setEditing(null);
    // Latest rows, so an edit committed as the drag began isn't lost (see moveKeynote)
    setRows((rs) => moveRowInAssembly(rs, groupRows(rs)[ai], rowIdx, g, q).rows);
    flashRows([rows[rowIdx].id]);
    setSelection({ r: newIdx, c: 0 });
  }

  // ── Drag ──
  // Pointer-based rather than HTML5 drag-and-drop: the merged Assembly and
  // Keynote cells (rowSpan) make native row dragging unreliable. React state
  // only changes when the drop slot changes; the ghost moves via its ref.
  // Two kinds, both confined to their assembly:
  //   "row"     — a line;           target { g, q, beforeIdx | afterIdx }
  //   "keynote" — a keynote group;  target { to, beforeIdx | afterIdx }
  const [drag, setDrag] = useState(null); // { kind, ai, rowIdx | ki, target | null }
  const dragRef = useRef(null);
  const ghostRef = useRef(null);
  const wrapRef = useRef(null);

  const rowRect = (i) =>
    wrapRef.current?.querySelector(`tr[data-row-idx="${i}"]`)?.getBoundingClientRect();

  function rowTargetFromPointer(ai, rowIdx, y) {
    // Lines of this assembly in visual order, skipping the dragged one
    const kns = asms[ai].keynotes;
    for (let g = 0; g < kns.length; g++) {
      const idxs = kns[g].indexes.filter((i) => i !== rowIdx);
      // A group holding only the dragged line has no other rows to aim at;
      // dropping onto its own (now empty) slot is a no-op anyway.
      for (let q = 0; q < idxs.length; q++) {
        const r = rowRect(idxs[q]);
        if (!r || y < r.top || y >= r.bottom) continue;
        const t = y < r.top + r.height / 2
          ? { g, q, beforeIdx: idxs[q] }
          : { g, q: q + 1, afterIdx: idxs[q] };
        return isSameSlot(ai, rowIdx, t.g, t.q) ? null : { ...t, key: `${t.g}:${t.q}` };
      }
    }
    // Over the dragged line itself (or a gap): keep it where it is
    return null;
  }

  function keynoteTargetFromPointer(ai, ki, y) {
    // Whole groups: upper half of a group = before it, lower half = after it
    const kns = asms[ai].keynotes;
    for (let g = 0; g < kns.length; g++) {
      if (g === ki) continue;
      const first = kns[g].indexes[0];
      const last = kns[g].indexes[kns[g].indexes.length - 1];
      const top = rowRect(first)?.top;
      const bottom = rowRect(last)?.bottom;
      if (top == null || bottom == null || y < top || y >= bottom) continue;
      const to = y < (top + bottom) / 2 ? g : g + 1;
      if (to === ki || to === ki + 1) return null; // where it already is
      return to < kns.length
        ? { to, beforeIdx: kns[to].indexes[0], key: String(to) }
        : { to, afterIdx: last, key: String(to) };
    }
    return null;
  }

  function onGripPointerDown(e, ai, rowIdx) {
    startDrag(e, {
      kind: "row", ai, rowIdx,
      findTarget: (y) => rowTargetFromPointer(ai, rowIdx, y),
      commit: (t) => moveRow(ai, rowIdx, t.g, t.q),
    });
  }

  function onKeynoteGripPointerDown(e, ai, ki) {
    startDrag(e, {
      kind: "keynote", ai, ki,
      findTarget: (y) => keynoteTargetFromPointer(ai, ki, y),
      commit: (t) => moveKeynote(ai, ki, t.to),
    });
  }

  function startDrag(e, { findTarget, commit: apply, ...info }) {
    if (e.button !== 0) return;
    const startY = e.clientY;
    const state = { started: false, y: e.clientY, x: e.clientX, raf: 0, target: null };
    dragRef.current = state;

    function updateTarget() {
      const t = findTarget(state.y);
      if ((t?.key ?? "") !== (state.target?.key ?? "")) {
        state.target = t;
        setDrag({ ...info, target: t });
      }
    }

    // Auto-scroll the grid while the pointer is near its top/bottom edge
    function autoScroll() {
      const wrap = wrapRef.current;
      if (!wrap || !state.started) return;
      const r = wrap.getBoundingClientRect();
      const head = wrap.querySelector("thead")?.getBoundingClientRect().height || 0;
      const EDGE = 40;
      let dy = 0;
      if (state.y < r.top + head + EDGE) dy = -Math.ceil((r.top + head + EDGE - state.y) / 4);
      else if (state.y > r.bottom - EDGE) dy = Math.ceil((state.y - (r.bottom - EDGE)) / 4);
      if (dy) {
        wrap.scrollTop += dy;
        updateTarget();
      }
      state.raf = requestAnimationFrame(autoScroll);
    }

    function onMove(ev) {
      state.x = ev.clientX;
      state.y = ev.clientY;
      if (!state.started) {
        if (Math.abs(ev.clientY - startY) < 4) return; // a click, not a drag yet
        state.started = true;
        document.body.classList.add("sow-dragging");
        window.getSelection()?.removeAllRanges();
        setEditing(null);
        setDrag({ ...info, target: null });
        state.raf = requestAnimationFrame(autoScroll);
      }
      if (ghostRef.current) {
        ghostRef.current.style.transform = `translate(${state.x + 12}px, ${state.y + 8}px)`;
      }
      updateTarget();
    }

    function finish(commit) {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("keydown", onKey, true);
      cancelAnimationFrame(state.raf);
      document.body.classList.remove("sow-dragging");
      dragRef.current = null;
      setDrag(null);
      if (state.started) swallowNextClick();
      if (commit && state.started && state.target) apply(state.target);
    }
    // Releasing over a cell makes the browser fire a click on it, which
    // would open that cell's (or keynote's) editor — eat it after a real drag
    function swallowNextClick() {
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);
    }
    function onUp() {
      finish(true);
    }
    function onKey(ev) {
      if (ev.key === "Escape") {
        ev.preventDefault();
        ev.stopPropagation();
        finish(false);
      }
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("keydown", onKey, true);
  }

  // If the table unmounts mid-drag, don't leave the page in drag mode
  useEffect(() => () => document.body.classList.remove("sow-dragging"), []);

  function moveSelection(r, c, dr, dc) {
    const nr = Math.max(0, Math.min(rows.length - 1, r + dr));
    const nc = Math.max(0, Math.min(numCols - 1, c + dc));
    setSelection({ r: nr, c: nc });
  }

  useEffect(() => {
    function onKey(e) {
      if (editing) return;
      if (!selection) return;
      const t = e.target;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      )
        return;
      const { r, c } = selection;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveSelection(r, c, 1, 0);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveSelection(r, c, -1, 0);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        moveSelection(r, c, 0, 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveSelection(r, c, 0, -1);
      } else if (e.key === "Tab") {
        e.preventDefault();
        moveSelection(r, c, 0, e.shiftKey ? -1 : 1);
      } else if (e.key === "Enter" || e.key === "F2") {
        e.preventDefault();
        setEditing({ r, c });
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        const col = columns[c];
        setRows((rs) => {
          const next = rs.slice();
          next[r] = { ...next[r], [col.key]: col.type === "spec" ? [] : "" };
          return next;
        });
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        setEditing({ r, c });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, editing, rows.length, columns]);

  const allChecked =
    rows.length > 0 && rows.every((_, i) => checkedRows.has(i));
  const someChecked = !allChecked && checkedRows.size > 0;
  const tableWidth =
    36 + 160 + 100 + columns.reduce((s, c) => s + c.width, 0);

  // Ghost label: a line shows the keynote it will join + its building element;
  // a keynote group shows its keynote + how many lines move with it
  let ghost = null;
  if (drag?.kind === "row" && rows[drag.rowIdx]) {
    const r = rows[drag.rowIdx];
    ghost = {
      keynote: (drag.target && asms[drag.ai]?.keynotes[drag.target.g]?.keynote) || r.keynote,
      text: r.element || (r.notUsed ? "Not used" : "New line"),
    };
  } else if (drag?.kind === "keynote" && asms[drag.ai]?.keynotes[drag.ki]) {
    const n = asms[drag.ai].keynotes[drag.ki].indexes.length;
    ghost = { keynote: asms[drag.ai].keynotes[drag.ki].keynote, text: `${n} line${n === 1 ? "" : "s"}` };
  }

  return (
    <div className="sow-grid-wrap" ref={wrapRef}>
      {ghost && (
        <div
          ref={(el) => {
            ghostRef.current = el;
            // Place it at the pointer straight away rather than at 0,0
            const s = dragRef.current;
            if (el && s) el.style.transform = `translate(${s.x + 12}px, ${s.y + 8}px)`;
          }}
          className="sow-drag-ghost"
        >
          <span className="sow-mono">{ghost.keynote}</span>
          <span className="sow-drag-ghost-text">{ghost.text}</span>
        </div>
      )}
      <table className="sow-grid" style={{ width: "100%", minWidth: tableWidth }}>
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 160 }} />
          <col style={{ width: 100 }} />
          {columns.map((c) => (
            <col key={c.key} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sow-th-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={() => {
                  if (allChecked) setCheckedRows(new Set());
                  else setCheckedRows(new Set(rows.map((_, i) => i)));
                }}
              />
            </th>
            <th>ASSEMBLY</th>
            <th>KEYNOTE</th>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3 + columns.length} className="sow-empty-state">
                Add an assembly to get started
              </td>
            </tr>
          )}
          {types.flatMap((type, ti) =>
            type.assemblies.flatMap((asm, asmInType) =>
              asm.keynotes.flatMap((kn, ki) =>
                kn.indexes.map((rowIdx, ri) => {
                  const row = rows[rowIdx];
                  const ai = asm.ai;
                  const firstOfAsm = ki === 0 && ri === 0;
                  const firstOfKn = ri === 0;
                  const subRowBtns = (
                    <div className="sow-reorder-btns">
                      <button
                        type="button"
                        className="sow-drag-handle"
                        title="Drag to move this line"
                        aria-label="Move line"
                        tabIndex={-1}
                        onPointerDown={(e) => onGripPointerDown(e, ai, rowIdx)}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  );
                  const t = drag?.target;
                  const rowDrag = drag?.kind === "row";
                  const knDrag = drag?.kind === "keynote";
                  return (
                    <tr
                      key={row.id || rowIdx}
                      data-row-idx={rowIdx}
                      className={[
                        checkedRows.has(rowIdx) ? "row-checked" : "",
                        movedRowIds.has(row.id) ? "sow-row-moved" : "",
                        rowDrag && drag.rowIdx === rowIdx ? "sow-row-dragging" : "",
                        knDrag && drag.ai === ai && drag.ki === ki ? "sow-kn-dragging" : "",
                        drag && drag.ai !== ai ? "sow-row-drag-disabled" : "",
                        rowDrag && t?.beforeIdx === rowIdx ? "sow-drop-before" : "",
                        rowDrag && t?.afterIdx === rowIdx ? "sow-drop-after" : "",
                        knDrag && t?.beforeIdx === rowIdx ? "sow-kn-drop-before" : "",
                        knDrag && t?.afterIdx === rowIdx ? "sow-drop-after" : "",
                      ].filter(Boolean).join(" ")}
                      onContextMenu={(e) => {
                        if (
                          e.target.closest(".sow-td-assembly, .sow-td-keynote")
                        )
                          return;
                        e.preventDefault();
                        onContextRow?.(rowIdx, e.clientX, e.clientY);
                      }}
                    >
                      <td className="sow-td-check">
                        <input
                          type="checkbox"
                          checked={checkedRows.has(rowIdx)}
                          onChange={(e) => {
                            const next = new Set(checkedRows);
                            if (e.target.checked) next.add(rowIdx);
                            else next.delete(rowIdx);
                            setCheckedRows(next);
                          }}
                        />
                      </td>

                      {firstOfAsm && (
                        <td
                          className="sow-td-assembly"
                          rowSpan={asm.totalRows}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onContextGroup?.(
                              { kind: "assembly", ai },
                              e.clientX,
                              e.clientY
                            );
                          }}
                        >
                          <div className="sow-merged-cell">
                            {asmInType === 0 && type.assembly_type_name && (
                              <div className="sow-assembly-type-name">
                                {type.assembly_type_name}
                              </div>
                            )}
                            <div className="sow-assembly-name-label">
                              {asm.assembly || (
                                <span className="sow-placeholder">
                                  Assembly name…
                                </span>
                              )}
                            </div>
                            <div className="sow-merged-actions">
                              <button
                                type="button"
                                className="sow-chip-add"
                                title="Add keynote to this assembly"
                                onClick={() => addKeynote(ai)}
                              >
                                <AddIcon sx={{ fontSize: 12 }} /> Keynote
                              </button>
                            </div>
                          </div>
                        </td>
                      )}

                      {firstOfKn && (
                        <td
                          className={`sow-td-keynote ${
                            row.notUsed && kn.indexes.length === 1
                              ? "sow-td-keynote-notused"
                              : ""
                          } ${rowDrag && drag.ai === ai && t?.g === ki ? "sow-td-keynote-drop-target" : ""} ${
                            // dropping a keynote group after the last one: line under this cell too
                            knDrag && drag.ai === ai && t?.afterIdx != null && ki === asm.keynotes.length - 1
                              ? "sow-td-keynote-drop-after"
                              : ""
                          }`}
                          rowSpan={kn.indexes.length}
                          onClick={(e) => {
                            if (e.target.closest("button, input")) return;
                            if (!editing) setEditing({ kind: "keynote", ai, ki });
                          }}
                        >
                          <div className="sow-merged-cell">
                            <div className="sow-keynote-main">
                              <button
                                type="button"
                                className="sow-drag-handle"
                                title="Drag to move this keynote"
                                aria-label="Move keynote"
                                tabIndex={-1}
                                onPointerDown={(e) => onKeynoteGripPointerDown(e, ai, ki)}
                              >
                                <DragIndicatorIcon sx={{ fontSize: 16 }} />
                              </button>
                              {editing &&
                              editing.kind === "keynote" &&
                              editing.ai === ai &&
                              editing.ki === ki ? (
                                <TextEditor
                                  value={kn.keynote}
                                  onCommit={(v) => commitKeynote(ai, ki, v)}
                                  onCancel={() => setEditing(null)}
                                />
                              ) : (
                                <div className="sow-keynote-label sow-mono">
                                  {kn.keynote || (
                                    <span className="sow-placeholder">{type.keynote_letter || "D"}—</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="sow-merged-actions">
                              <button
                                type="button"
                                className="sow-chip-add"
                                title="Add sub-row"
                                onClick={() => addSubRow(ai, ki)}
                              >
                                <AddIcon sx={{ fontSize: 12 }} /> Sub-row
                              </button>
                            </div>
                          </div>
                        </td>
                      )}

                      {row.notUsed ? (
                        <NotUsedCells
                          columns={columns}
                          rowIdx={rowIdx}
                          row={row}
                          setRows={setRows}
                          setSelection={setSelection}
                          selection={selection}
                          setEditing={setEditing}
                          editing={editing}
                          commitCell={commitCell}
                          onCategoryCreateNew={onCategoryCreateNew}
                          reorderBtns={subRowBtns}
                        />
                      ) : (
                        columns.map((col, ci) => (
                          <Cell
                            key={col.key}
                            col={col}
                            colIdx={ci}
                            rowIdx={rowIdx}
                            value={row[col.key]}
                            row={row}
                            selection={selection}
                            setSelection={setSelection}
                            editing={editing}
                            setEditing={setEditing}
                            onCommit={commitCell}
                            onCreateNew={onCategoryCreateNew}
                            reorderBtns={ci === 0 ? subRowBtns : null}
                          />
                        ))
                      )}
                    </tr>
                  );
                })
              )
            )
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SKELETON_WIDTHS = [65, 80, 50, 75, 60, 70, 55, 85];

function ScopeTableSkeleton({ columns }) {
  const tableWidth = 36 + 160 + 100 + columns.reduce((s, c) => s + c.width, 0);
  return (
    <div className="sow-grid-wrap">
      <table className="sow-grid" style={{ width: "100%", minWidth: tableWidth }}>
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 160 }} />
          <col style={{ width: 100 }} />
          {columns.map((c) => (
            <col key={c.key} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sow-th-check" />
            <th>ASSEMBLY</th>
            <th>KEYNOTE</th>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 7 }).map((_, ri) => (
            <tr key={ri}>
              <td className="sow-td-check" />
              <td className="sow-td-assembly">
                <div className="sow-merged-cell">
                  <div className="sow-skeleton-bar" style={{ width: `${SKELETON_WIDTHS[ri % SKELETON_WIDTHS.length]}%` }} />
                </div>
              </td>
              <td className="sow-td-keynote">
                <div className="sow-merged-cell">
                  <div className="sow-skeleton-bar" style={{ width: "45%" }} />
                </div>
              </td>
              {columns.map((c, ci) => (
                <td key={c.key} className="sow-cell">
                  <div className="sow-cell-content">
                    <div className="sow-skeleton-bar" style={{ width: `${SKELETON_WIDTHS[(ri + ci + 1) % SKELETON_WIDTHS.length]}%` }} />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function keynoteGroupExtent(rows, r) {
  const row = rows[r];
  let first = r, last = r;
  for (let i = r - 1; i >= 0; i--) {
    if (rows[i].assembly_id === row.assembly_id && rows[i].keynote === row.keynote) first = i;
    else break;
  }
  for (let i = r + 1; i < rows.length; i++) {
    if (rows[i].assembly_id === row.assembly_id && rows[i].keynote === row.keynote) last = i;
    else break;
  }
  return { first, last };
}

// ─── ContextMenu ──────────────────────────────────────────────────────────────

function ContextMenu({ menu, rows, setRows, onClose, setCheckedRows, onBeforeDeleteRows }) {
  const ref = useRef(null);
  useEffect(() => {
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function onEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const asms = groupRows(rows);
  function close() { onClose(); }
  function commit(rs) { setRows(rs); close(); }

  let items = [];

  if (menu.kind === "row") {
    const r = menu.r;
    const row = rows[r];
    items = [
      {
        label: "Insert row above",
        onClick: () => {
          const { first } = keynoteGroupExtent(rows, r);
          commit([...rows.slice(0, first), blankRow(rows, r), ...rows.slice(first)]);
        },
      },
      {
        label: "Insert row below",
        onClick: () => {
          const { last } = keynoteGroupExtent(rows, r);
          commit([...rows.slice(0, last + 1), blankRow(rows, r), ...rows.slice(last + 1)]);
        },
      },
      !row.notUsed
        ? {
            label: "Mark as NOT USED",
            onClick: () => {
              const next = rows.slice();
              next[r] = { ...next[r], notUsed: true };
              commit(next);
            },
          }
        : null,
      { divider: true },
      {
        label: "Delete row",
        danger: true,
        onClick: () => {
          onBeforeDeleteRows?.([r]);
          commit(rows.filter((_, i) => i !== r));
        },
      },
    ].filter(Boolean);
  } else if (menu.kind === "assembly") {
    const a = asms[menu.ai];
    if (!a) return null;
    const firstIdx = a.keynotes[0].indexes[0];
    const lastIdx = a.keynotes[a.keynotes.length - 1].indexes.at(-1);
    const templateRow = rows[firstIdx];
    items = [
      {
        label: "Add keynote",
        onClick: () => {
          const letter = a.keynote_letter || "D";
          const next = rows.slice();
          next.splice(lastIdx + 1, 0, {
            id: makeId(),
            assembly: a.assembly,
            assembly_id: templateRow?.assembly_id || null,
            assembly_type_id: a.assembly_type_id || null,
            assembly_type_name: a.assembly_type_name || "",
            keynote_letter: letter,
            scope_of_work_order: a.scope_of_work_order ?? 0,
            keynote: suggestKeynote(rows, letter),
            sort_order: 0,
            category: "",
            element: "",
            core_repair_scope_id: null,
            building_element_id: null,
            description: "",
            material: "",
            specs: [],
            details: "",
          });
          commit(next);
        },
      },
      { divider: true },
      {
        label: `Delete assembly (${a.totalRows} row${
          a.totalRows === 1 ? "" : "s"
        })`,
        danger: true,
        onClick: () => {
          const allIndexes = a.keynotes.flatMap((kn) => kn.indexes);
          onBeforeDeleteRows?.(allIndexes);
          commit(rows.filter((_, i) => i < firstIdx || i > lastIdx));
          setCheckedRows(new Set());
        },
      },
    ];
  }

  return (
    <div
      className="sow-row-menu"
      style={{ left: menu.x, top: menu.y }}
      ref={ref}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((it, i) =>
        it.divider ? (
          <div key={i} className="sow-menu-sep" />
        ) : (
          <button
            key={i}
            className={it.danger ? "danger" : ""}
            onClick={it.onClick}
          >
            {it.label}
          </button>
        )
      )}
    </div>
  );
}

// ─── Version history ─────────────────────────────────────────────────────────


function formatVersionDate(date) {
  return (
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) +
    " at " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

function VersionHistoryDrawer({ open, onClose, exports, onDownload }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {open && <div className="sow-drawer-backdrop" onClick={onClose} />}
      <div className={`sow-drawer${open ? " sow-drawer-open" : ""}`}>
        <div className="sow-drawer-header">
          <span className="sow-drawer-title">Export History</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className="sow-drawer-body">
          {exports.length === 0 ? (
            <div className="sow-drawer-empty">No exported versions yet.</div>
          ) : (
            exports.map((v) => (
              <div key={v.id} className="sow-version-item">
                <div className="sow-version-main">
                  <div className="sow-version-date">
                    {formatVersionDate(new Date(v.created))}
                  </div>
                  <div className="sow-version-user">
                    <span className="sow-version-name">{v.employee_name}</span>
                  </div>
                </div>
                <div className="sow-version-actions">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => onDownload(v)}
                  >
                    Download
                  </Button>
                  <div className="sow-version-format">
                    {v.format_excel ? "Excel (.xlsx)" : "Word (.docx)"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function allSelectedAreNotUsed(rows, checkedRows) {
  if (checkedRows.size === 0) return false;
  for (const i of checkedRows) {
    if (!rows[i]?.notUsed) return false;
  }
  return true;
}

export default function ScopeOfWork() {
  const { id } = useParams();
  const { instance, accounts } = useMsal();
  const employeeID = useContext(UserContext);
  const hist = useHistory([]);

  const [isLoading, setIsLoading] = useState(true);
  const [jobRecord, setJobRecord] = useState(null);
  const [assembliesData, setAssembliesData] = useState([]);
  const [scopesData, setScopesData] = useState([]);
  const [specsData, setSpecsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [exportsData, setExportsData] = useState([]);

  const rowDbIdsRef = useRef({});
  const pendingDeletesRef = useRef([]);
  const autosaveTimerRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const handleSaveRef = useRef(null);
  const [autosaveState, setAutosaveState] = useState(null); // null | "saving" | { savedAt: Date }

  useEffect(() => {
    postAPICall(instance, accounts[0], "/api/logs/access/add", {
      page_name: "Scope Of Work",
      employeeID: employeeID,
      notes: "Accessed the Scope Of Work page",
    }).catch(() => {});
  }, []);

  const columns = useMemo(() => [
    {
      key: "category",
      label: "CATEGORY",
      type: "select",
      options: categoriesData,
      width: 120,
      pill: true,
      createNew: true,
      createNewLabel: "+ Create new category",
      searchPlaceholder: "Search categories…",
    },
    {
      key: "element",
      label: "BUILDING ELEMENT",
      type: "scope",
      scopes: scopesData,
      width: 210,
    },
    {
      key: "description",
      label: "DESCRIPTION OF WORK",
      type: "richtext",
      width: 300,
    },
    { key: "material", label: "REPLACEMENT MATERIAL", type: "richtext", width: 210 },
    {
      key: "specs",
      label: "SPEC SECTION",
      type: "spec",
      specs: specsData,
      width: 260,
    },
    { key: "details", label: "DETAILS", type: "longtext", width: 140 },
  ], [scopesData, specsData, categoriesData]);

  // Load all reference data and existing SOW records
  useEffect(() => {
    const fetchJobRecord = async () => {
      try {
        const res = await getAPICall(
          instance,
          accounts[0],
          "/api/jobs/design/byID",
          { id }
        );
        if (res.data?.[0]) setJobRecord(res.data[0]);
      } catch (err) {
        console.log("Error fetching design job record:", err);
      }
    };

    const loadData = async () => {
      try {
        const [
          assembliesRes,
          sowRes,
          specsJunctionRes,
          crsRes,
          rssRes,
          allSpecsRes,
        ] = await Promise.all([
          getAPICall(instance, accounts[0], "/api/encompass/assemblies/all"),
          getAPICall(instance, accounts[0], "/api/scopeOfWork/byPhaseID", { phaseID: id }),
          getAPICall(instance, accounts[0], "/api/scopeOfWork/specs/byPhaseID", { phaseID: id }),
          getAPICall(instance, accounts[0], "/api/coreRepairScopes/all"),
          getAPICall(instance, accounts[0], "/api/coreRepairScopes/repairScopeSpecifications/all"),
          getAPICall(instance, accounts[0], "/api/specifications/allStandardSpecifications"),
        ]);

        setAssembliesData(assembliesRes.data || []);

        // Build real specs array sorted by division
        const rawSpecs = (allSpecsRes.data || [])
          .map((s) => ({ id: s.id, division: s.division, title: s.title }))
          .sort((a, b) => a.division.localeCompare(b.division, undefined, { numeric: true }));
        setSpecsData(rawSpecs);

        // Build scopes with resolved specs from the junction table
        const rssJunction = rssRes.data || [];
        const crsTransformed = (crsRes.data || []).map((crs) => {
          const specs = rssJunction
            .filter((j) => j.core_repair_scopes_id === crs.id)
            .map((j) => rawSpecs.find((s) => s.id === j.spec_id))
            .filter(Boolean);
          return {
            id: crs.id,
            name: crs.description,
            description: crs.description,
            material: crs.replacement_material,
            buildingType: crs.building_type,
            buildingElementId: crs.building_element_id,
            specs,
          };
        });
        setScopesData(crsTransformed);

        // Load existing SOW rows
        const sowRows = sowRes.data || [];
        if (sowRows.length === 0) return;

        const sowSpecsByRowId = {};
        for (const spec of specsJunctionRes.data || []) {
          if (!sowSpecsByRowId[spec.scope_of_work_id]) {
            sowSpecsByRowId[spec.scope_of_work_id] = [];
          }
          sowSpecsByRowId[spec.scope_of_work_id].push({
            id: spec.specifications_id,
            label: `${spec.division} — ${spec.title}`,
          });
        }
        for (const arr of Object.values(sowSpecsByRowId)) {
          arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        }

        const loadedRows = sowRows.map((dbRow) => {
          const localId = makeId();
          rowDbIdsRef.current[localId] = dbRow.id;
          const matchingScope = crsTransformed.find(
            (s) => s.id === dbRow.core_repair_scope_id
          );
          const matchingAssembly = (assembliesRes.data || []).find(
            (a) => a.id === dbRow.assembly_id
          );
          return {
            id: localId,
            assembly: dbRow.assembly_name || "",
            assembly_id: dbRow.assembly_id,
            assembly_type_id: matchingAssembly?.assembly_type_id || null,
            assembly_type_name: matchingAssembly?.assembly_type_name || "",
            keynote_letter: matchingAssembly?.keynote_letter || "D",
            scope_of_work_order: matchingAssembly?.scope_of_work_order ?? 0,
            keynote: dbRow.keynote || "",
            sort_order: dbRow.sort_order,
            category: dbRow.category || "",
            element: dbRow.building_element_name || "",
            core_repair_scope_id: dbRow.core_repair_scope_id,
            building_element_id: dbRow.building_element_id,
            notUsed: !!dbRow.is_not_used,
            description: dbRow.description || "",
            material: dbRow.replacement_material || "",
            specs: sowSpecsByRowId[dbRow.id] || [],
            availableSpecs: matchingScope?.specs || [],
            details: dbRow.details || "",
          };
        });

        // Assign sequential sort_order within each keynote group based on loaded order.
        // This normalizes NULL values from rows that existed before the sort_order column
        // was added, and ensures sort_order is always clean and sequential.
        const sortCounters = {};
        const loadedRowsWithSort = loadedRows.map((row) => {
          const key = `${row.assembly_id}_${row.keynote}`;
          sortCounters[key] = (sortCounters[key] || 0) + 1;
          return { ...row, sort_order: sortCounters[key] };
        });

        skipAutosaveRef.current = true;
        hist.reset(loadedRowsWithSort);
      } catch (err) {
        console.log("Error loading scope of work data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobRecord();
    loadData();
    fetchCategories();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = hist.state;
  const setRows = useCallback((updater) => {
    hist.set((rs) => {
      const next = typeof updater === "function" ? updater(rs) : updater;
      return renumberKeynotes(next);
    });
  }, [hist.set]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selection, setSelection] = useState({ r: 0, c: 0 });
  const [editing, setEditing] = useState(null);
  const [checkedRows, setCheckedRows] = useState(new Set());
  const [menu, setMenu] = useState(null);
  const [assemblyPickerOpen, setAssemblyPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createCategoryPendingRow, setCreateCategoryPendingRow] = useState(null);
  const [createAssemblyOpen, setCreateAssemblyOpen] = useState(false);

  useEffect(() => {
    if (!historyOpen) return;
    getAPICall(instance, accounts[0], "/api/scopeOfWork/exports/byPhaseID", { phaseID: id })
      .then((res) => setExportsData(res.data || []))
      .catch((err) => console.log("Error loading exports:", err));
  }, [historyOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDownload(record) {
    try {
      const ext = record.format_excel ? "xlsx" : "docx";
      const propName = (jobRecord?.property_name || "scope").replace(/[^a-z0-9-_ ]/gi, " ").trim();
      const title = `SOW ${propName}.${ext}`;
      const res = await getAPICall(
        instance,
        accounts[0],
        "/api/scopeOfWork/exports/byS3Name",
        { s3Name: record.s3_name, title }
      );
      window.open(res.data, "_blank");
    } catch (err) {
      console.log("Error downloading export:", err);
    }
  }
  async function fetchAssemblies() {
    try {
      const res = await getAPICall(instance, accounts[0], "/api/encompass/assemblies/all");
      setAssembliesData(res.data || []);
      return res.data || [];
    } catch (err) {
      console.log("Error fetching assemblies:", err);
      return [];
    }
  }

  function handleAssemblyCreateNew() {
    setAssemblyPickerOpen(false);
    setCreateAssemblyOpen(true);
  }

  async function onAssemblyDialogClose(refresh, newName) {
    setCreateAssemblyOpen(false);
    if (refresh) {
      const updated = await fetchAssemblies();
      if (newName) {
        const newRecord = updated.find((a) => a.name === newName);
        const letter = newRecord?.keynote_letter || "D";
        setRows((rs) => {
          const newRow = {
            id: makeId(),
            assembly: newName,
            assembly_id: newRecord?.id || null,
            assembly_type_id: newRecord?.assembly_type_id || null,
            assembly_type_name: newRecord?.assembly_type_name || "",
            keynote_letter: letter,
            scope_of_work_order: newRecord?.scope_of_work_order ?? 0,
            keynote: suggestKeynote(rs, letter),
            sort_order: 0,
            category: "",
            element: "",
            core_repair_scope_id: null,
            building_element_id: null,
            description: "",
            material: "",
            specs: [],
            details: "",
          };
          return insertAssemblyRow(rs, newRow);
        });
      }
    }
  }

  async function fetchCategories() {
    try {
      const res = await getAPICall(instance, accounts[0], "/api/encompass/designCategories/all");
      const titles = (res.data || []).map((c) => c.title);
      if (titles.length > 0) setCategoriesData(titles);
    } catch (err) {
      console.log("Error fetching design categories:", err);
    }
  }

  function handleCategoryCreateNew(rowIdx) {
    setCreateCategoryPendingRow(rowIdx);
    setCreateCategoryOpen(true);
  }

  function onCategoryDialogClose(refresh, newTitle) {
    const pendingRow = createCategoryPendingRow;
    setCreateCategoryOpen(false);
    setCreateCategoryPendingRow(null);
    if (refresh) {
      fetchCategories();
      if (newTitle && pendingRow != null) {
        setRows((rs) => {
          const next = rs.slice();
          next[pendingRow] = { ...next[pendingRow], category: newTitle };
          return next;
        });
      }
    }
  }

  const [isSaving, setIsSaving] = useState(false);
  const addAssemblyBtnRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        hist.undo();
      } else if (
        (e.key.toLowerCase() === "z" && e.shiftKey) ||
        e.key.toLowerCase() === "y"
      ) {
        e.preventDefault();
        hist.redo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hist.undo, hist.redo]);

  useEffect(() => {
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    setAutosaveState("unsaved");
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      handleSaveRef.current?.();
    }, 3000);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  function recordPendingDeletes(rowIndexes) {
    for (const i of rowIndexes) {
      const dbId = rowDbIdsRef.current[rows[i]?.id];
      if (dbId) pendingDeletesRef.current.push(dbId);
    }
  }

  function deleteSelectedRows() {
    if (checkedRows.size === 0) return;
    recordPendingDeletes([...checkedRows]);
    setRows((rs) => rs.filter((_, i) => !checkedRows.has(i)));
    setCheckedRows(new Set());
  }

  function markNotUsed(val) {
    setRows((rs) =>
      rs.map((r, i) => (checkedRows.has(i) ? { ...r, notUsed: val } : r))
    );
    setCheckedRows(new Set());
  }

  async function handleSave() {
    setIsSaving(true);
    setAutosaveState("saving");
    try {
      const phaseID = id;

      // Delete rows that were removed since last save
      for (const dbId of pendingDeletesRef.current) {
        await deleteAPICall(
          instance,
          accounts[0],
          "/api/scopeOfWork/specs/deleteByScopeOfWorkID",
          { scopeOfWorkID: dbId }
        );
        await deleteAPICall(instance, accounts[0], "/api/scopeOfWork/delete", {
          id: dbId,
        });
      }
      pendingDeletesRef.current = [];

      // Upsert each row
      for (const row of rows) {
        const dbId = rowDbIdsRef.current[row.id];

        const rowData = {
          phaseID,
          assemblyID: row.assembly_id || null,
          keynote: row.keynote || "",
          category: row.category || "",
          coreRepairScopeID: row.core_repair_scope_id || null,
          buildingElementID: row.building_element_id || null,
          isNotUsed: row.notUsed ? 1 : 0,
          description: row.description || "",
          replacementMaterial: row.material || "",
          details: row.details || "",
          sortOrder: row.sort_order ?? null,
        };

        let savedDbId = dbId;

        if (dbId) {
          await putAPICall(
            instance,
            accounts[0],
            "/api/scopeOfWork/update",
            { ...rowData, id: dbId },
            employeeID
          );
        } else {
          const res = await postAPICall(
            instance,
            accounts[0],
            "/api/scopeOfWork/add",
            rowData,
            employeeID
          );
          savedDbId = res.data?.insertId;
          if (savedDbId) rowDbIdsRef.current[row.id] = savedDbId;
        }

        // Sync spec junction: clear then re-insert current spec (preserved even when not used)
        if (savedDbId) {
          await deleteAPICall(
            instance,
            accounts[0],
            "/api/scopeOfWork/specs/deleteByScopeOfWorkID",
            { scopeOfWorkID: savedDbId }
          );
          for (const spec of row.specs || []) {
            await postAPICall(
              instance,
              accounts[0],
              "/api/scopeOfWork/specs/add",
              { scopeOfWorkID: savedDbId, specificationsID: spec.id },
              employeeID
            );
          }
        }
      }
      setAutosaveState({ savedAt: new Date() });
    } catch (err) {
      console.log("Error saving scope of work:", err);
      setAutosaveState(null);
    }
    setIsSaving(false);
  }
  handleSaveRef.current = handleSave;

  async function exportXLSX() {
    const title = [
      "SCOPE OF WORK AND COMPONENT REPLACEMENT SCHEDULE",
      jobRecord?.property_name,
    ]
      .filter(Boolean)
      .join(" — ");

    const headers = [
      "ASSEMBLY",
      "KEYNOTE",
      "CATEGORY",
      "BUILDING ELEMENT",
      "DESCRIPTION OF WORK",
      "REPLACEMENT MATERIAL",
      "SPECIFICATION SECTION",
      "APPLICABLE DETAILS",
    ];

    const up = (s) => (s || "").toUpperCase();
    const specText = (specs) => {
      const sorted = [...(specs || [])].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
      return sorted.map((s) => s.label.replace(" — ", " ").toUpperCase()).join(";\n");
    };

    const aoa = [[title.toUpperCase()], headers];
    for (const r of rows) {
      aoa.push([
        up(r.assembly_type_name),
        up(r.keynote),
        up(r.category),
        r.notUsed ? "NOT USED" : up(r.element),
        r.notUsed ? "" : up(stripHtml(r.description)),
        r.notUsed ? "" : up(stripHtml(r.material)),
        r.notUsed ? "" : specText(r.specs),
        r.notUsed ? "" : up(r.details),
      ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!cols"] = [
      { wch: 15.2 },
      { wch: 8.5 },
      { wch: 15.7 },
      { wch: 26.8 },
      { wch: 50.3 },
      { wch: 30.8 },
      { wch: 35.7 },
      { wch: 25.7 },
    ];

    ws["!rows"] = [
      { hpt: 24 },
      { hpt: 15.75 },
      ...rows.map(() => ({ hpt: 60 })),
    ];

    const rowOff = 2;
    const lastColIdx = headers.length - 1;
    const lastDataRowIdx = rowOff + rows.length - 1;

    // Build merges and track assembly type group boundaries for border styling
    const asms = groupRows(rows);
    const types = groupTypes(asms);
    const groupTopRows = new Set([rowOff]);
    const notUsedRows = new Set(
      rows.map((r, i) => r.notUsed ? rowOff + i : -1).filter((i) => i >= 0)
    );
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastColIdx } }];

    // Column 0: merge by assembly type
    let typeCursor = 0;
    for (const t of types) {
      if (t.totalRows > 1) {
        ws["!merges"].push({
          s: { r: rowOff + typeCursor, c: 0 },
          e: { r: rowOff + typeCursor + t.totalRows - 1, c: 0 },
        });
      }
      typeCursor += t.totalRows;
      if (typeCursor < rows.length) groupTopRows.add(rowOff + typeCursor);
    }

    // Column 1: merge by keynote within each assembly; handle notUsed spans
    let cursor = 0;
    for (const a of asms) {
      let kCursor = cursor;
      for (const k of a.keynotes) {
        if (k.indexes.length > 1) {
          ws["!merges"].push({
            s: { r: rowOff + kCursor, c: 1 },
            e: { r: rowOff + kCursor + k.indexes.length - 1, c: 1 },
          });
        }
        k.indexes.forEach((rowIdx, idx) => {
          if (rows[rowIdx].notUsed) {
            ws["!merges"].push({
              s: { r: rowOff + kCursor + idx, c: 3 },
              e: { r: rowOff + kCursor + idx, c: lastColIdx },
            });
          }
        });
        kCursor += k.indexes.length;
      }
      cursor += a.totalRows;
    }

    // Apply cell styles
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "z", v: "" };

        const isTitle = r === 0;
        const isHeader = r === 1;

        const bold = isTitle || isHeader || c <= 2;
        const sz = isTitle ? 18 : 11;
        const isNotUsed = notUsedRows.has(r) && c === 3;
        const horizontal = isTitle || isHeader || c <= 2 || isNotUsed ? "center" : "left";
        const vertical = !isTitle && !isHeader && c <= 2 ? "center" : "top";

        const topStyle =
          isTitle || isHeader || groupTopRows.has(r) ? "medium" : "thin";
        const bottomStyle =
          isTitle || isHeader || r === lastDataRowIdx ? "medium" : "thin";
        const leftStyle = c === 0 || c === 1 ? "medium" : "thin";
        const rightStyle = c === 0 || c === lastColIdx ? "medium" : "thin";

        ws[addr].s = {
          font: { name: "Calibri", sz, bold },
          alignment: { horizontal, vertical, wrapText: true },
          border: {
            top: { style: topStyle },
            bottom: { style: bottomStyle },
            left: { style: leftStyle },
            right: { style: rightStyle },
          },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scope");
    const filename =
      `SOW ${(jobRecord?.property_name || "scope").replace(/[^a-z0-9-_ ]/gi, " ").trim()}.xlsx`;

    // Generate buffer instead of writing directly to disk
    const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Trigger local browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    // Upload to S3 and save DB record
    try {
      const formData = new FormData();
      formData.append("file", blob, filename);
      formData.append("phaseID", id);
      formData.append("formatExcel", "1");
      formData.append("modifiedBy", employeeID);
      await postFormDataAPICall(
        instance,
        accounts[0],
        "/api/scopeOfWork/exports/add",
        formData
      );

      // Refresh exports list
      const refreshRes = await getAPICall(
        instance,
        accounts[0],
        "/api/scopeOfWork/exports/byPhaseID",
        { phaseID: id }
      );
      setExportsData(refreshRes.data || []);
    } catch (err) {
      console.log("Error uploading exported scope of work:", err);
    }
  }

  async function exportDocx() {
    // ── Styling constants ──────────────────────────────────────────────────────
    const F_HEADING = "Myriad Pro Cond"; // section heading + col 1 data
    const F_DATA    = "Calibri";         // col 2-3 data
    const SZ_CELL   = 21;  // 10.5 pt in half-points
    const SZ_SEC    = 24;  // 12 pt in half-points

    const COL_W   = [1944, 7230, 1626]; // twips: item | description | category
    const TABLE_W = 10800;              // total table width in twips

    const BORDER    = { style: BorderStyle.SINGLE, size: 4, color: "7F7F7F" };
    const NO_BORDER = { style: BorderStyle.NONE,   size: 0, color: "auto"   };
    const BAND      = { fill: "F2F2F2", type: ShadingType.CLEAR, color: "auto" };

    const ALL_NONE = { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER, insideHorizontal: NO_BORDER, insideVertical: NO_BORDER };

    // ── Build sections from live row data ─────────────────────────────────────
    const TYPE_DISPLAY_MAP = {
      "Roof":          { heading: "ROOF",               colHeader: "ROOF ITEM"                },
      "Attic":         { heading: "ATTIC",              colHeader: "ATTIC ITEM"               },
      "Fenestrations": { heading: "WINDOWS & DOORS",    colHeader: "WINDOWS & DOORS ITEM"     },
      "Walls":         { heading: "WALLS",              colHeader: "WALL ITEM"                },
      "Decks":         { heading: "DECKS & LANDINGS",   colHeader: "DECKS & LANDINGS ITEM"    },
      "Misc":          { heading: "MISCELLANEOUS",      colHeader: "MISCELLANEOUS ITEM"       },
    };

    const sectionMap = new Map();
    for (const r of rows) {
      if (r.notUsed) continue;
      const key = r.assembly_type_id ?? `__no_type_${r.assembly_type_name}`;
      if (!sectionMap.has(key)) {
        const typeName = r.assembly_type_name || "";
        const display = TYPE_DISPLAY_MAP[typeName] || { heading: typeName.toUpperCase(), colHeader: typeName.toUpperCase() + " ITEM" };
        sectionMap.set(key, { heading: display.heading, colHeader: display.colHeader, rows: [] });
      }
      sectionMap.get(key).rows.push({
        item: r.element || "",
        description: stripHtml(r.description || ""),
        category: r.category || "",
      });
    }
    const SECTIONS = Array.from(sectionMap.values()).filter((s) => s.rows.length > 0);

    // ── Cell builders ──────────────────────────────────────────────────────────
    function headerCell(text, colIdx) {
      return new TableCell({
        width: { size: COL_W[colIdx], type: WidthType.DXA },
        margins: { top: 0, bottom: 0, left: 108, right: 108 },
        verticalAlign: VerticalAlign.CENTER,
        borders: { top: NO_BORDER, left: NO_BORDER, right: NO_BORDER, bottom: BORDER },
        children: [
          new Paragraph({
            children: [new TextRun({ text: text.toUpperCase(), bold: true, font: { ascii: F_HEADING, hAnsi: F_HEADING }, size: SZ_CELL })],
            spacing: { before: 60, after: 60 },
          }),
        ],
      });
    }

    function dataCell(text, colIdx, rowIdx) {
      const isItem  = colIdx === 0;
      const isBand  = rowIdx % 2 === 0;
      return new TableCell({
        width: { size: COL_W[colIdx], type: WidthType.DXA },
        margins: { top: 0, bottom: 0, left: 108, right: 108 },
        shading: isBand ? BAND : undefined,
        borders: { top: NO_BORDER, left: NO_BORDER, bottom: NO_BORDER, right: isItem ? BORDER : NO_BORDER },
        children: [
          new Paragraph({
            children: [new TextRun({ text: text || "", bold: isItem, allCaps: isItem, font: { ascii: isItem ? F_HEADING : F_DATA, hAnsi: isItem ? F_HEADING : F_DATA }, size: SZ_CELL })],
            spacing: { before: 60, after: 60 },
          }),
        ],
      });
    }

    // ── Build document children ────────────────────────────────────────────────
    const children = [];
    for (let si = 0; si < SECTIONS.length; si++) {
      const sec = SECTIONS[si];
      children.push(
        new Paragraph({
          children: [new TextRun({ text: sec.heading, bold: true, font: { ascii: F_HEADING, hAnsi: F_HEADING }, size: SZ_SEC })],
          spacing: { before: si > 0 ? 280 : 0, after: 160, line: 259, lineRule: "auto" },
        })
      );
      children.push(
        new Table({
          width: { size: TABLE_W, type: WidthType.DXA },
          columnWidths: COL_W,
          borders: ALL_NONE,
          rows: [
            new TableRow({
              tableHeader: true,
              children: [
                headerCell(sec.colHeader, 0),
                headerCell("DESCRIPTION OF WORK", 1),
                headerCell("CATEGORY", 2),
              ],
            }),
            ...sec.rows.map((r, ri) =>
              new TableRow({
                children: [
                  dataCell(r.item,        0, ri),
                  dataCell(r.description, 1, ri),
                  dataCell(r.category,    2, ri),
                ],
              })
            ),
          ],
        })
      );
    }

    // ── Assemble and download ──────────────────────────────────────────────────
    const doc = new Document({
      sections: [{
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { top: 720, bottom: 1440, left: 720, right: 720, header: 0, footer: 0, gutter: 0 },
          },
        },
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const propName = (jobRecord?.property_name || "scope").replace(/[^a-z0-9-_ ]/gi, " ").trim();
    const filename = `SOW ${propName}.docx`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    try {
      const formData = new FormData();
      formData.append("file", blob, filename);
      formData.append("phaseID", id);
      formData.append("formatExcel", "0");
      formData.append("modifiedBy", employeeID);
      await postFormDataAPICall(instance, accounts[0], "/api/scopeOfWork/exports/add", formData);
      const refreshRes = await getAPICall(instance, accounts[0], "/api/scopeOfWork/exports/byPhaseID", { phaseID: id });
      setExportsData(refreshRes.data || []);
    } catch (err) {
      console.log("Error uploading exported scope of work (docx):", err);
    }
  }

  const usedAssemblies = new Set(rows.map((r) => r.assembly).filter(Boolean));
  const availableAssemblies = assembliesData
    .filter((a) => !usedAssemblies.has(a.name))
    .map((a) => a.name);

  function addAssembly(assemblyName) {
    const assemblyRecord = assembliesData.find((a) => a.name === assemblyName);
    const letter = assemblyRecord?.keynote_letter || "D";
    setRows((rs) => {
      const newRow = {
        id: makeId(),
        assembly: assemblyName,
        assembly_id: assemblyRecord?.id || null,
        assembly_type_id: assemblyRecord?.assembly_type_id || null,
        assembly_type_name: assemblyRecord?.assembly_type_name || "",
        keynote_letter: letter,
        scope_of_work_order: assemblyRecord?.scope_of_work_order ?? 0,
        keynote: suggestKeynote(rs, letter),
        category: "",
        element: "",
        core_repair_scope_id: null,
        building_element_id: null,
        description: "",
        material: "",
        specs: [],
        details: "",
      };
      return insertAssemblyRow(rs, newRow);
    });
  }

  return (
    <div className="ScopeOfWork" onClick={() => setMenu(null)}>
      <div className="sowHeader">
        <div className="sowHeaderTitle">
          SCOPE OF WORK AND COMPONENT REPLACEMENT SCHEDULE
          {jobRecord?.property_name ? ` — ${jobRecord.property_name}` : ""}
        </div>
        <div className="sowHeaderBottom">
          <div className="sowHeaderSubtitle">
            {jobRecord?.job_name ?? ""}
          </div>
          <div className="sowToolbar">
        {checkedRows.size === 0 && (
          <>
            {autosaveState === "unsaved" && (
              <span className="sow-save-status">Unsaved changes</span>
            )}
            {autosaveState === "saving" && (
              <span className="sow-save-status">
                <CircularProgress
                  size={11}
                  thickness={5}
                  sx={{ color: "var(--sow-text-secondary)" }}
                />
                Saving…
              </span>
            )}
            {autosaveState?.savedAt && (
              <span className="sow-save-status">
                <CheckIcon sx={{ fontSize: 13, color: "var(--sow-text-secondary)" }} />
                Saved {formatSaveTime(autosaveState.savedAt)}
              </span>
            )}
          </>
        )}

        <div className="sowIbar">
          <IconButton
            size="small"
            disabled={!hist.canUndo}
            onClick={hist.undo}
            title="Undo (Ctrl+Z)"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={!hist.canRedo}
            onClick={hist.redo}
            title="Redo (Ctrl+Shift+Z)"
          >
            <RedoIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setHistoryOpen(true)}
            title="Export history"
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </div>

        {checkedRows.size > 0 ? (
          <div className="sowBulk">
            <span className="sowBulkCount">{checkedRows.size} selected</span>
            {allSelectedAreNotUsed(rows, checkedRows) ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => markNotUsed(false)}
              >
                Unmark NOT USED
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => markNotUsed(true)}
              >
                Mark NOT USED
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={deleteSelectedRows}
            >
              Delete rows
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportXLSX}
              disabled={rows.length === 0}
            >
              Export .xlsx
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              disabled={rows.length === 0}
              onClick={exportDocx}
            >
              Export .docx
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              Save
            </Button>
          </div>
        )}
          </div>{/* sowToolbar */}
        </div>{/* sowHeaderBottom */}
      </div>{/* sowHeader */}

      <div className="sowCanvas">
        {isLoading ? (
          <ScopeTableSkeleton columns={columns} />
        ) : (
          <ScopeTable
            columns={columns}
            rows={rows}
            setRows={setRows}
            selection={selection}
            setSelection={setSelection}
            editing={editing}
            setEditing={setEditing}
            checkedRows={checkedRows}
            setCheckedRows={setCheckedRows}
            onContextRow={(r, x, y) => setMenu({ kind: "row", r, x, y })}
            onContextGroup={(info, x, y) => setMenu({ ...info, x, y })}
            onCategoryCreateNew={handleCategoryCreateNew}
          />
        )}

        <div className="sowFooter">
          <button
            ref={addAssemblyBtnRef}
            className="sow-btn-add-assembly"
            onClick={() => setAssemblyPickerOpen(true)}
          >
            <AddIcon sx={{ fontSize: 16 }} /> Add assembly
          </button>
          {assemblyPickerOpen && (
            <SelectEditor
              value={null}
              options={availableAssemblies}
              onCommit={(v) => {
                addAssembly(v);
                setAssemblyPickerOpen(false);
              }}
              onCancel={() => setAssemblyPickerOpen(false)}
              anchorRef={addAssemblyBtnRef}
              onCreateNew={handleAssemblyCreateNew}
              createNewLabel="+ Create new assembly"
              searchPlaceholder="Search assemblies…"
            />
          )}
        </div>
      </div>

      {menu && (
        <ContextMenu
          menu={menu}
          rows={rows}
          setRows={setRows}
          onClose={() => setMenu(null)}
          setCheckedRows={setCheckedRows}
          onBeforeDeleteRows={recordPendingDeletes}
        />
      )}

      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        exports={exportsData}
        onDownload={handleDownload}
      />

      <DesignCategoriesEditAdd
        open={createCategoryOpen}
        editing={false}
        recordToEdit={null}
        onClose={onCategoryDialogClose}
      />

      <AssembliesConsoleEditAdd
        open={createAssemblyOpen}
        editing={false}
        recordToEdit={null}
        onClose={onAssemblyDialogClose}
      />
    </div>
  );
}
